import React, { useState } from 'react'
import { makeStyles } from '@material-ui/core'
import { Typography, Link, Button, Tooltip, InvoiceStatus, Avatar, ProviderPopover } from 'components'
import TableCell from '@material-ui/core/TableCell'
import { colors, CONSTANTS, routes } from 'constants/index'
import * as moment from 'moment'
import { Link as RouterLink } from 'react-router-dom'
import PeopleWithAccessModal from '../../cases/PeopleWithAccessModal'

const useStyles = makeStyles({
  link: {
    color: colors.BLUE_20,
    textDecoration: 'none',
    fontSize: 14,
  },
  tableCellTextClass: {
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    overflow: 'hidden',
  },
  payNowBtn: {
    minWidth: 74,
    padding: '8px 12px',
    fontSize: 11,
    minHeight: 28,
  },
  payNowCell: {
    maxWidth: 105,
    textAlign: 'center',
  },
  clientInvoiceRow: {
    display: 'flex',
    alignItems: 'center',
  },
  tooltip: {
    fontSize: 13,
    fontWeight: 'normal',
  },
  icon: {
    lineHeight: 0,
  },
  providersContainer: {
    display: 'flex',
    alignItems: 'center',
    paddingLeft: 0,
    paddingRight: 8,
    justifyContent: 'center'
  },
  providerAvatar: {
    width: 24,
    height: 24,
    fontSize: 9.75,
    fontWeight: 400,
    marginRight: -8,
    cursor: 'pointer',
    border: `2px solid ${colors.WHITE}`,
    '&:last-child': {
      marginRight: 0,
    },
  },
  moreProvidersAvatar: {
    width: 24,
    height: 24,
    fontSize: 9.75,
    fontWeight: 400,
    marginRight: -8,
    cursor: 'pointer',
    border: `2px solid ${colors.WHITE}`,
    backgroundColor: colors.BASIC.COLOR[80],
    color: colors.WHITE,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    lineHeight: '13.5px',
    textTransform: 'uppercase',
  },
})

const getInitials = (firstName, lastName) => {
  const first = firstName ? firstName.charAt(0).toUpperCase() : ''
  const last = lastName ? lastName.charAt(0).toUpperCase() : ''
  return first + last || '?'
}

const ClientInvoicesTableRow = ({ data }) => {
  const classes = useStyles()
  const [providerPopoverAnchor, setProviderPopoverAnchor] = useState(null)
  const [selectedProvider, setSelectedProvider] = useState(null)
  const [showPeopleModal, setShowPeopleModal] = useState(false)

  const invoiceDate = moment(data.invoiceDate).format(CONSTANTS.DATE_FORMAT.FORMAT_ONE)
  const getCurrencySymbol = () => {
    return data?.currency === 'CAD' ? 'C$' : '$'
  }

  const contributors = data.contributors || []
  const hasMultipleProviders = contributors.length >= 3
  const visibleProviders = hasMultipleProviders ? contributors.slice(0, 2) : contributors
  const remainingCount = hasMultipleProviders ? contributors.length - 2 : 0

  const handleProviderAvatarClick = (provider, e) => {
    e.stopPropagation()
    setSelectedProvider(provider)
    setProviderPopoverAnchor(e.currentTarget)
  }

  const handleProviderPopoverClose = () => {
    setProviderPopoverAnchor(null)
    setSelectedProvider(null)
  }

  const handleMoreProvidersClick = e => {
    e.stopPropagation()
    setShowPeopleModal(true)
  }

  return (
    <>
      <TableCell>
        <Typography variant="body2" classes={{ root: classes.tableCellTextClass }}>
          <Link
            className={classes.link}
            component={RouterLink}
            to={`${routes.getClientAccountInvoiceDetailsViewPath()}/${data.uuid}`}
          >
            {data.invoiceNumber}
          </Link>
        </Typography>
      </TableCell>
      <TableCell>
        {contributors.length > 0 && (
          <div className={classes.providersContainer}>
            {visibleProviders.map((provider, index) => (
              <Avatar
                key={provider.username || index}
                className={classes.providerAvatar}
                src={provider.photo_thumbnail || provider.photo || null}
                onClick={e => handleProviderAvatarClick(provider, e)}
              >
                {!provider.photo_thumbnail && !provider.photo && getInitials(provider.first_name, provider.last_name)}
              </Avatar>
            ))}
            {hasMultipleProviders && (
              <Avatar className={classes.moreProvidersAvatar} onClick={handleMoreProvidersClick}>
                +{remainingCount}
              </Avatar>
            )}
          </div>
        )}
        {selectedProvider && (
          <ProviderPopover
            provider={selectedProvider}
            anchorEl={providerPopoverAnchor}
            open={Boolean(providerPopoverAnchor)}
            onClose={handleProviderPopoverClose}
          />
        )}
        <PeopleWithAccessModal
          isOpen={showPeopleModal}
          onClose={() => setShowPeopleModal(false)}
          providers={contributors}
        />
      </TableCell>
      <TableCell>
        <Typography variant="body2" classes={{ root: classes.tableCellTextClass }}>
          {invoiceDate}
        </Typography>
      </TableCell>
      <TableCell>{data.proFullName}</TableCell>
      <TableCell align={'right'}>
        <Typography variant="body2" classes={{ root: classes.tableCellTextClass }}>
          {getCurrencySymbol()} {data.amountDue}
        </Typography>
      </TableCell>
      <TableCell align={'right'}>
        <Typography variant="body2" classes={{ root: classes.tableCellTextClass }}>
          {getCurrencySymbol()} {data.amountPaid}
        </Typography>
      </TableCell>
      <TableCell classes={{ root: classes.payNowCell }}>
        {data.isPayButtonState === CONSTANTS.PAY_NOW_BUTTON_STATE.SHOWED && (
          <Button
            className={classes.payNowBtn}
            component={RouterLink}
            to={`${routes.getClientInvoicePaymentLinkPath()}/${data.uuid}`}
          >
            pay now
          </Button>
        )}
        {data.isPayButtonState === CONSTANTS.PAY_NOW_BUTTON_STATE.SHOWED_AND_DISABLED && (
          <Tooltip
            title={'Automatic charge will be performed in less than 5 min'}
            tooltipClass={classes.tooltip}
            interactive
          >
            <Button className={classes.payNowBtn} disabled>
              pay now
            </Button>
          </Tooltip>
        )}
      </TableCell>
      <TableCell>
        <InvoiceStatus status={data.statusForDisplay} title={data.failureReason} clientStatus />
      </TableCell>
    </>
  )
}

export default ClientInvoicesTableRow
